import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Circle, MapContainer, Marker, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";
import { ArrowLeft, Check, MapPin, Navigation, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { colorForUser, initialsOf, isFamilyContact } from "../lib/colors";
import { GEOFENCE_SLOTS as SLOTS, mapGeofencesToSlots } from "../lib/geofenceSlots.mjs";

function makeContactIcon(contact) {
  const bg = isFamilyContact(contact) ? "#FF2D55" : colorForUser(contact.user_id);
  const html = renderToStaticMarkup(
    <div className="proximity-pin">
      <div className="pulse" style={{ background: `${bg}55` }} />
      <div className="letter" style={{ background: bg, color: "#fff", borderColor: "#fff" }}>
        {initialsOf(contact.name)}
      </div>
    </div>
  );
  return L.divIcon({ html, className: "", iconSize: [44, 44], iconAnchor: [22, 22] });
}

function makeFenceIcon(slot) {
  return L.divIcon({
    html: `<div style="width:32px;height:32px;border-radius:50%;background:#FFCC00;color:#18181b;border:3px solid white;display:grid;place-items:center;font-weight:900;box-shadow:0 4px 16px #0008">${slot}</div>`,
    className: "",
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

function FlyTo({ center }) {
  const map = useMap();
  const lat = center?.[0];
  const lng = center?.[1];
  useEffect(() => {
    if (lat != null && lng != null) map.flyTo([lat, lng], 15, { duration: 0.5 });
  }, [lat, lng, map]);
  return null;
}

function PickLocation({ enabled, onPick }) {
  useMapEvents({
    click(event) {
      if (enabled) onPick({ lat: event.latlng.lat, lng: event.latlng.lng });
    },
  });
  return null;
}

export default function GeofenceEditor() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const returnToDashboard = search.get("return") === "dashboard";
  const [contact, setContact] = useState(null);
  const [fences, setFences] = useState([]);
  const [slot, setSlot] = useState("A");
  const [name, setName] = useState("");
  const [radius, setRadius] = useState(200);
  const [point, setPoint] = useState(null);
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const bySlot = useMemo(() => mapGeofencesToSlots(fences), [fences]);
  const selectedFence = bySlot[slot] || null;
  const contactPoint = useMemo(() => (
    contact?.latest
      ? { lat: Number(contact.latest.lat), lng: Number(contact.latest.lng) }
      : null
  ), [contact?.latest]);

  const exit = useCallback(() => {
    navigate(returnToDashboard ? `/dashboard?focus=${encodeURIComponent(contactId)}` : "/settings");
  }, [contactId, navigate, returnToDashboard]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [contactsResponse, fencesResponse] = await Promise.all([
        api.get("/contacts"),
        api.get("/geofences"),
      ]);
      const found = (contactsResponse.data || []).find((item) => item.user_id === contactId);
      if (!found) {
        toast.error("Contact not found");
        exit();
        return;
      }
      setContact(found);
      setFences((fencesResponse.data || []).filter((item) => item.contact_user_id === contactId));
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not load geofences");
    } finally {
      setLoading(false);
    }
  }, [contactId, exit]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const fence = bySlot[slot];
    if (fence) {
      setName(fence.name || "");
      setRadius(Number(fence.radius_m) || 200);
      setPoint({ lat: Number(fence.lat), lng: Number(fence.lng) });
      setPicking(false);
    } else {
      setName("");
      setRadius(200);
      setPoint(contactPoint);
      setPicking(false);
    }
  }, [slot, bySlot, contactPoint]);

  const chooseContactLocation = () => {
    if (!contactPoint) {
      toast.error(`${contact?.name || "This contact"} has not shared a location yet`);
      return;
    }
    setPoint(contactPoint);
    setPicking(false);
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name this geofence"); return; }
    if (!point) { toast.error("Choose a location on the map"); return; }
    setSaving(true);
    try {
      if (selectedFence) {
        await api.patch(`/geofences/${selectedFence.geofence_id}`, {
          name: name.trim(), radius_m: Number(radius),
        });
        toast.success(`${slot}: ${name.trim()} updated`);
      } else {
        await api.post("/geofences", {
          name: name.trim(), lat: point.lat, lng: point.lng,
          radius_m: Number(radius), contact_user_id: contactId,
          slot, enabled: true,
        });
        toast.success(`${slot}: ${name.trim()} created`);
      }
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save geofence");
    } finally { setSaving(false); }
  };

  const toggle = async () => {
    if (!selectedFence) return;
    setSaving(true);
    try {
      await api.patch(`/geofences/${selectedFence.geofence_id}`, { enabled: selectedFence.enabled === false });
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update geofence");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!selectedFence || !window.confirm(`Delete ${slot}: ${selectedFence.name}?`)) return;
    setSaving(true);
    try {
      await api.delete(`/geofences/${selectedFence.geofence_id}`);
      toast.message(`${slot}: ${selectedFence.name} removed`);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not delete geofence");
    } finally { setSaving(false); }
  };

  const center = point || contactPoint || { lat: 39.5, lng: -98.35 };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-zinc-950 text-zinc-50">
      <div className="absolute inset-0 z-0">
        <MapContainer center={[center.lat, center.lng]} zoom={point || contactPoint ? 15 : 4} style={{ height: "100vh", width: "100%" }} zoomControl={false}>
          <TileLayer attribution='&copy; OpenStreetMap &copy; CARTO' url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
          <FlyTo center={[center.lat, center.lng]} />
          <PickLocation enabled={picking && !selectedFence} onPick={(next) => { setPoint(next); setPicking(false); }} />
          {contactPoint && <Marker position={[contactPoint.lat, contactPoint.lng]} icon={makeContactIcon(contact)} />}
          {point && <>
            <Circle center={[point.lat, point.lng]} radius={Number(radius)} pathOptions={{ color: selectedFence?.enabled === false ? "#71717a" : "#FFCC00", fillColor: "#FFCC00", fillOpacity: 0.16, weight: 3, dashArray: "8 5" }} />
            <Marker position={[point.lat, point.lng]} icon={makeFenceIcon(slot)} />
          </>}
        </MapContainer>
      </div>

      <button data-testid="gf-back" onClick={exit} className="absolute top-4 left-4 z-20 glass size-11 rounded-full grid place-items-center"><ArrowLeft size={18} /></button>
      <div className="absolute top-4 left-20 right-4 z-20 glass rounded-2xl px-4 py-3">
        <div className="label-eyebrow">Manage geofences</div>
        <div className="font-display font-bold truncate">{contact?.name || (loading ? "Loading…" : "Contact")}</div>
      </div>

      <div className="absolute left-4 right-4 bottom-5 z-20 glass rounded-2xl p-4 max-h-[62vh] overflow-y-auto">
        <div className="grid grid-cols-3 gap-2" aria-label="Geofence slots">
          {SLOTS.map((candidate) => {
            const fence = bySlot[candidate];
            return <button key={candidate} data-testid={`gf-slot-${candidate}`} onClick={() => setSlot(candidate)} className={`rounded-xl border px-3 py-2 text-left ${slot === candidate ? "border-[#FFCC00] bg-[#FFCC00]/15" : "border-white/10 bg-black/20"}`}>
              <span className="font-black text-[#FFCC00]">{candidate}</span>
              <span className="block truncate text-xs text-zinc-300">{fence?.name || "Available"}</span>
              {fence && <span className={`block text-[10px] ${fence.enabled === false ? "text-zinc-500" : "text-[#34C759]"}`}>{fence.enabled === false ? "Off" : "Active"}</span>}
            </button>;
          })}
        </div>

        <div className="mt-3">
          <label className="label-eyebrow" htmlFor="gf-name-input">Name for {slot}</label>
          <input id="gf-name-input" data-testid="gf-name-input" value={name} onChange={(event) => setName(event.target.value.slice(0, 60))} placeholder="School, Home, Bar…" className="mt-1.5 w-full bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FFCC00]" />
        </div>

        {!selectedFence && <div className="mt-3 grid grid-cols-2 gap-2">
          <button data-testid="gf-use-contact-location" onClick={chooseContactLocation} disabled={!contactPoint} className="rounded-xl border border-white/10 px-3 py-2 text-xs font-bold disabled:opacity-40"><Navigation size={13} className="inline mr-1" />Use current location</button>
          <button data-testid="gf-pick-map-location" onClick={() => setPicking(true)} className={`rounded-xl border px-3 py-2 text-xs font-bold ${picking ? "border-[#FFCC00] text-[#FFCC00]" : "border-white/10"}`}><MapPin size={13} className="inline mr-1" />{picking ? "Tap the map" : "Choose on map"}</button>
        </div>}
        {selectedFence && <div className="mt-2 text-[11px] text-zinc-500">This saved location stays fixed. Delete this slot to choose a different location.</div>}

        <div className="mt-3 flex items-center gap-3">
          <span className="label-eyebrow whitespace-nowrap">Radius</span>
          <input data-testid="gf-radius-slider" type="range" min="50" max="5000" step="50" value={radius} onChange={(event) => setRadius(event.target.value)} className="flex-1 accent-[#FFCC00]" />
          <span className="font-mono text-sm w-16 text-right">{radius}m</span>
        </div>

        <div className="mt-4 flex gap-2">
          <button data-testid="gf-save-btn" onClick={save} disabled={saving || loading} className="flex-1 bg-[#FFCC00] text-zinc-950 font-black py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50"><Check size={17} />{selectedFence ? "Save changes" : `Create ${slot}`}</button>
          {selectedFence && <>
            <button data-testid="gf-toggle-btn" onClick={toggle} disabled={saving} title={selectedFence.enabled === false ? "Enable" : "Disable"} className="size-12 rounded-full border border-white/10 grid place-items-center"><Power size={16} className={selectedFence.enabled === false ? "text-zinc-500" : "text-[#34C759]"} /></button>
            <button data-testid="gf-delete-btn" onClick={remove} disabled={saving} title="Delete" className="size-12 rounded-full border border-white/10 grid place-items-center text-[#FF3B30]"><Trash2 size={16} /></button>
          </>}
        </div>
      </div>
    </div>
  );
}
